import React, { useState, memo, useMemo, useEffect } from 'react';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import Loading from '../reusableElementsComponents/Loading';
import * as Constants from '../../constants';
import * as Utils from '../../Utilities';
import TabsMenu from "../reusableElementsComponents/TabsMenu";
import NewPositionGeneralEdit from '../../components/newOpenPositionComponents/NewPositionGeneralEdit';
import NewPositionAssessmentsEdit from '../../components/newOpenPositionComponents/NewPositionAssessmentsEdit';
import WriteAccessGuard from '../reusableElementsComponents/WriteAccessGuard';
import Button from "@mui/material/Button";
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Modal from '@mui/material/Modal';
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import B2BApiService from "../../B2bApiService";
import { t } from '../../content';

const OpenPositionEdit = ({ positionData, onSave }) => {

    let [poolGeneralData, setPoolGeneralData] = useState({});
    let [poolAssessmentData, setPoolAssessmentData] = useState([]);
    let [poolDataEditedFlag, setPoolDataEditedFlag] = useState(false);
    let [deletePoolModalShow, setDeletePoolModalShow] = useState(false);

    useEffect(() => {
        if (positionData) {
            setPoolGeneralData({
                title: positionData.title,
                deadline: positionData.deadline ? new Date(positionData.deadline) : null,
                description: positionData.description,
                industry: positionData.industry,
                target_profile: positionData.target_profile,
                members: positionData.members || [],
            });
            setPoolAssessmentData(positionData.assessments || []);
        }
    }, [positionData]);

    const b2bServiceObj = useMemo(() => {
        return new B2BApiService();
    }, []);

    const navigate = useNavigate();

    const template = {
        tabs: [
            {
                component: <NewPositionGeneralEdit data={poolGeneralData} updateData={setPoolGeneralData} poolHash={positionData.hash} setPoolDataEditedFlag={setPoolDataEditedFlag} poolAssessments={positionData.assessments || []} />,
                sectionTitle: t('teams.tabs.general')
            },
            {
                component: <NewPositionAssessmentsEdit data={poolAssessmentData} updateData={setPoolAssessmentData} poolHash={positionData.hash} setPoolDataEditedFlag={setPoolDataEditedFlag} />,
                sectionTitle: t('teams.tabs.assessments')
            }
        ]
    };

    const savePool = async () => {
        const mydate = new Date(poolGeneralData.deadline);
        poolGeneralData.deadline = `${mydate.getFullYear()}/${mydate.getMonth() + 1}/${mydate.getDate()} 00:00:00`;
        let member_list = poolGeneralData.members || positionData.members || [];
        // Extract only necessary fields: email, first, last
        member_list = member_list.map(member => ({ email: member.email, first: member.first, last: member.last }));
        const targetProfileId = poolGeneralData.target_profile_id?.toString() || poolGeneralData.target_profile?.id?.toString();
        if (!targetProfileId) {
            toast.error(t('recruitment.form.targetProfileError'), {containerId: "poolManagementToast"});
            return;
        }
        const values = {
            title: poolGeneralData.title,
            description: poolGeneralData.description,
            deadline: poolGeneralData.deadline,
            industry_id: poolGeneralData.industry_id?.toString() || poolGeneralData.industry.id.toString(),
            target_profile_id: targetProfileId,
            member_list: member_list,
            assessments: poolAssessmentData || positionData.assessments,
            hash: positionData.hash,
        };

        try {
            const response = await b2bServiceObj.UpdatePool(values);
            if (response.ok && response.httpStatus === 200) {
                onSave();
                toast.success(t('recruitment.toasts.updated'), {containerId: "poolManagementToast"});
            } else {
                toast.error(t('recruitment.toasts.updateError'), {containerId: "poolManagementToast"});
            }
        } catch (error) {
            toast.error(t('recruitment.toasts.networkError'), {containerId: "poolManagementToast"});
            console.error(error);
        } finally {
            setPoolDataEditedFlag(false);
        }
    };

    const deletePool = () => {
        try {
            b2bServiceObj.DeletePool(positionData?.hash).then((response) => {
                if (response && response.httpStatus === 200) {
                    toast.success(t('recruitment.toasts.deleted'), {containerId: "poolManagementToast"});
                    setDeletePoolModalShow(false);
                    window.location.reload()
                } else {
                    toast.error(t('recruitment.toasts.deleteError'), {containerId: "poolManagementToast"});
                }
            }).catch((error) => {
                console.error("Error deleting pool: ", error);
                toast.error(t('recruitment.toasts.deleteSystemError'), {containerId: "poolManagementToast"});
            });
        } catch (error) {
            console.error("Error in delete pool function: ", error);
            toast.error(t('recruitment.toasts.deleteUnexpected'), {containerId: "poolManagementToast"});
        }
    }

    if (positionData) {
        return (
            <div className="open-positions-edit">
                <div className="flex flex-row justify-content-space-between" style={{marginBottom:"30px"}}>
                    <Button
                        className={'mazhrBtn light-green'}
                        onClick={() => {
                            navigate(`/talents/${positionData.hash}`);
                            localStorage.setItem(Constants.LOCAL_STORAGE_KEY_SEARCH_TYPE, Constants.SEARCH_TYPE_TARGET_POSITION);
                        }}>
                        <VisibilityIcon /> &nbsp; {t('recruitment.viewPoolCandidates')}
                    </Button>
                    
                    &nbsp;&nbsp;&nbsp;

                    {![Constants.USER_ROLE_TEAMLEAD, Constants.USER_ROLE_SPECIALIST].includes(Utils.currentUserRole()) && (
                        <div>
                            <WriteAccessGuard>
                                <Button className={`mazhrBtn default-green ${poolDataEditedFlag ? 'blinking-border-button' : ''}`} onClick={savePool} disabled={!poolDataEditedFlag}>
                                    <SaveIcon /> &nbsp; {t('recruitment.savePool')}
                                </Button>
                            </WriteAccessGuard>
                            {![Constants.USER_ROLE_MANAGER].includes(Utils.currentUserRole()) &&
                                <WriteAccessGuard>
                                    <Button className="mazhrBtn default-red mar-45 left" onClick={() => setDeletePoolModalShow(true)}>
                                        <DeleteForeverIcon/> &nbsp; {t('common.actions.delete')}
                                    </Button>
                                </WriteAccessGuard>
                            }
                        </div>
                    )}
                </div>

                <TabsMenu template={template} activeKey={() => {}} />

                {poolDataEditedFlag && <div className="unsaved-changes">{t('teams.unsavedChanges')} <span className="cta-txt">{t('teams.unsavedChangesCta')}</span></div>}

                <Modal
                    open={deletePoolModalShow}
                    title={''}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                    slotProps={{
                        backdrop: {
                            sx: {
                                height: "100vh",
                            },
                        },
                    }}
                >
                    <div style={{ 
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 'fit-content',
                        minWidth: '500px',
                        height: 'fit-content',
                        minHeight: '500px',
                        backgroundColor: 'white',
                        padding: '30px',
                        textAlign: 'center',
                        alignContent: 'center',
                        // alignItems: 'center',
                        borderRadius: '10px',
                    }}>
                        {/* {loading ? <img src={loadingImage} alt="Loading..." /> : <> */}
                            <h4>{t('recruitment.deleteConfirmTitle', { title: positionData?.title })}</h4>
                            <p className="red">{t('recruitment.deleteConfirmCannotUndo')}
                                <br/>
                                {t('recruitment.deleteConfirmData')}
                                <br/>
                                {t('recruitment.deleteConfirmTalentAccess')}
                            </p>
                            <div  className="flex justify-content-center">
                                <button className="mazhrBtn default-green white mar-25 right" onClick={deletePool}>{t('common.actions.confirm')}</button>
                                <button  className="mazhrBtn light-grey white" onClick={() => setDeletePoolModalShow(false)}>{t('common.actions.cancel')}</button>
                            </div>
                        {/* </>
                        } */}
                    </div>
                </Modal>

            </div>
        );
    } else {
        return <Loading />;
    }
};

export default memo(OpenPositionEdit);
